/**
 * Package Form Component
 * Handles creating and editing credit packages
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { type FieldErrors, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Save, X } from 'lucide-react';

import { packageFormSchema, type PackageFormValues } from '@/src/schemas/admin/package-schema';
import { usePackage, usePackageMutations } from '@/src/hooks/admin/usePackages';
import { EXPIRY_PERIODS } from '@/src/constants/admin/packages';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import {
  BasicInfoSection,
  PricingSection,
  FeaturesSection,
  PermissionsSection,
} from './form-sections';

export default function PackageForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const [submitErrorDialogOpen, setSubmitErrorDialogOpen] = useState(false);
  const [submitErrorTitle, setSubmitErrorTitle] = useState('ไม่สามารถบันทึกแพ็กเกจได้');
  const [submitErrorMessages, setSubmitErrorMessages] = useState<string[]>([]);

  // Fetch existing package data if editing
  const { package: existingPackage, loading: loadingPackage } = usePackage(id || '');
  const { createPackage, updatePackage, loading: saving } = usePackageMutations();

  // Initialize form
  const form = useForm<PackageFormValues>({
    resolver: zodResolver(packageFormSchema),
    defaultValues: {
      package_code: '',
      name: '',
      description: '',
      is_active: true,
      sort_order: 50,
      monthly_price: 0,
      monthly_price_usd: 0,
      included_credits: 0,
      overage_rate: 0,
      overage_rate_usd: 0,
      max_credit_purchase: 0,
      credit_expiry_period: EXPIRY_PERIODS[0],
      features: [],
      permissions: [],
    },
  });

  // Populate form when editing
  useEffect(() => {
    if (isEditMode && existingPackage) {
      form.reset({
        package_code: existingPackage.package_code,
        name: existingPackage.name,
        description: existingPackage.description || '',
        is_active: existingPackage.is_active,
        sort_order: existingPackage.sort_order,
        monthly_price: existingPackage.monthly_price,
        monthly_price_usd: existingPackage.monthly_price_usd ?? undefined,
        included_credits: existingPackage.included_credits,
        overage_rate: existingPackage.overage_rate,
        overage_rate_usd: existingPackage.overage_rate_usd ?? undefined,
        max_credit_purchase: existingPackage.max_credit_purchase,
        credit_expiry_period: existingPackage.credit_expiry_period,
        features: existingPackage.features,
        permissions: existingPackage.permissions,
      });
    }
  }, [existingPackage, isEditMode, form]);

  // Handle form submission
  const onSubmit = async (data: PackageFormValues) => {
    console.log('📤 [PackageForm] onSubmit:', {
      isEditMode,
      packageId: id,
      permissions: data.permissions,
      features: data.features,
      fullData: data
    });
    
    try {
      if (isEditMode && id) {
        await updatePackage(id, data);
      } else {
        await createPackage(data);
      }
      navigate('/admin/packages');
    } catch (error: any) {
      const apiErrorMessage = error?.response?.data?.error;
      const apiDetails = error?.response?.data?.details;
      const fallback = 'โปรดตรวจสอบข้อมูลที่กรอก และลองใหม่อีกครั้ง';
      const messages = Array.isArray(apiDetails) && apiDetails.length > 0
        ? apiDetails
        : [apiErrorMessage || fallback];

      setSubmitErrorTitle('บันทึกแพ็กเกจไม่สำเร็จ');
      setSubmitErrorMessages(messages);
      setSubmitErrorDialogOpen(true);
      console.error('Failed to save package:', error);
    }
  };

  const collectFormErrorMessages = (errors: FieldErrors<PackageFormValues>): string[] => {
    const messages: string[] = [];

    const walk = (value: unknown) => {
      if (!value || typeof value !== 'object') return;

      if ('message' in (value as { message?: unknown }) && typeof (value as { message?: unknown }).message === 'string') {
        messages.push((value as { message: string }).message);
      }

      if (Array.isArray(value)) {
        value.forEach(walk);
        return;
      }

      Object.values(value as Record<string, unknown>).forEach(walk);
    };

    walk(errors);
    return [...new Set(messages)];
  };

  const onInvalidSubmit = (errors: FieldErrors<PackageFormValues>) => {
    const messages = collectFormErrorMessages(errors);
    setSubmitErrorTitle('ข้อมูลไม่ครบหรือไม่ถูกต้อง');
    setSubmitErrorMessages(
      messages.length > 0 ? messages : ['โปรดตรวจสอบข้อมูลที่กรอกให้ถูกต้องก่อนกดบันทึก']
    );
    setSubmitErrorDialogOpen(true);
  };

  // Handle cancel
  const handleCancel = () => {
    navigate('/admin/packages');
  };

  if (loadingPackage) {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardContent className="py-8">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <span className="ml-2">กำลังโหลดข้อมูล...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 max-w-5xl">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={handleCancel}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          กลับไปหน้ารายการ
        </Button>
        <h1 className="text-3xl font-bold">
          {isEditMode ? 'แก้ไขแพ็กเกจ' : 'สร้างแพ็กเกจใหม่'}
        </h1>
        <p className="text-muted-foreground mt-2">
          {isEditMode
            ? 'แก้ไขข้อมูลแพ็กเกจเครดิตและคุณสมบัติต่างๆ'
            : 'สร้างแพ็กเกจเครดิตใหม่พร้อมกำหนดราคา คุณสมบัติ และสิทธิ์การใช้งาน'}
        </p>
      </div>

      {/* Form */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, onInvalidSubmit)} className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>ข้อมูลพื้นฐาน</CardTitle>
              <CardDescription>
                ข้อมูลทั่วไปของแพ็กเกจ
              </CardDescription>
            </CardHeader>
            <CardContent>
              <BasicInfoSection form={form} isEditMode={isEditMode} />
            </CardContent>
          </Card>

          {/* Pricing */}
          <Card>
            <CardHeader>
              <CardTitle>ราคาและเครดิต</CardTitle>
              <CardDescription>
                กำหนดราคารายเดือน เครดิตที่รวมในแพ็กเกจ และอัตราเครดิตเพิ่มเติม
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PricingSection form={form} />
            </CardContent>
          </Card>

          {/* Features */}
          <Card>
            <CardHeader>
              <CardTitle>คุณสมบัติ</CardTitle>
              <CardDescription>
                กำหนดฟีเจอร์ที่มีในแพ็กเกจนี้
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FeaturesSection form={form} />
            </CardContent>
          </Card>

          {/* Permissions */}
          <Card>
            <CardHeader>
              <CardTitle>สิทธิ์การใช้งาน</CardTitle>
              <CardDescription>
                กำหนดสิทธิ์ API ที่ลูกค้าสามารถเรียกใช้ได้
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PermissionsSection form={form} />
            </CardContent>
          </Card>

          {/* Form Actions */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-end gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  <X className="mr-2 h-4 w-4" />
                  ยกเลิก
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                >
                  <Save className="mr-2 h-4 w-4" />
                  {saving ? 'กำลังบันทึก...' : isEditMode ? 'บันทึกการแก้ไข' : 'สร้างแพ็กเกจ'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </Form>

      <AlertDialog open={submitErrorDialogOpen} onOpenChange={setSubmitErrorDialogOpen}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{submitErrorTitle}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>ไม่สามารถ submit package form ได้ เนื่องจาก:</p>
                <ul className="list-disc pl-5 space-y-1">
                  {submitErrorMessages.map((message, idx) => (
                    <li key={`${message}-${idx}`}>{message}</li>
                  ))}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setSubmitErrorDialogOpen(false)}>
              รับทราบ
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
